import React from 'react'

import { ConfirmationModal, ModalActionTypes, Spinner } from '@acorns/web-components'

import AlertBadge from 'assets/icons/alert-badge.png'
import DollarImage from 'assets/icons/dollar-green.svg'
import PortfolioImage from 'assets/icons/portfolio.svg'
import SpendImage from 'assets/icons/spend-tungsten-metal.svg'
import ThumbsUpImage from 'assets/icons/thumbs-up.svg'
import LaterLogoImage from 'assets/images/later-logo.png'
import Button from 'components/Button'
import { getRemoteStrings } from 'components/HOCs/withRemoteStrings/withRemoteStrings'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { TestIDs } from 'test/utils/testIds'

import { enhance } from './enhance'
import {
  Container,
  FloatingHeader,
  Header,
  Image,
  ImageContainer,
  ImageIcon,
  MenuContainer,
  MenuItem,
  SubscriptionSubText,
  SubscriptionText,
  Text,
  TextItem,
  TextItemContainer,
  Title,
} from './styled-components'
import { Props } from './types'

const getTextItems = props => [
  {
    imageSource: DollarImage,
    text: props.subTextOne,
    alt: 'generic dollar bill icon',
  },
  {
    imageSource: ThumbsUpImage,
    text: props.subTextTwo,
    alt: 'generic thumbs up icon',
  },
  {
    imageSource: PortfolioImage,
    text: props.subTextThree,
    alt: 'generic pie chart icon',
  },
]
const ItemContainer = ({ imgSrc, text, alt }) => (
  <TextItem>
    <ImageIcon>
      <img src={imgSrc} width="36px" height="36px" alt={alt} />
    </ImageIcon>
    <Text>{text}</Text>
  </TextItem>
)

const LaterOnboardingWelcome: React.SFC<Props> = (props: Props) => {
  const items = getTextItems(props)
  return (
    <>
      <Nav back={true} />
      <Layout>
        <Container>
          <ImageContainer>
            <Image src={LaterLogoImage} data-testid={TestIDs.WELCOME.LATER_ONBOARDING.HERO_IMAGE} />
          </ImageContainer>
          <Title data-testid={TestIDs.WELCOME.LATER_ONBOARDING.TITLE}>{props.title}</Title>
          {props.isTieredRegEnabled && (
            <>
              {props.subscriptionIncludesLater && (
                <SubscriptionText
                  data-testid={TestIDs.WELCOME.LATER_ONBOARDING.SUBSCRIPTION_INCLUDES_LATER_TEXT}
                >
                  {props.subscriptionDetails}
                </SubscriptionText>
              )}
            </>
          )}
          <SubscriptionSubText data-testid={TestIDs.WELCOME.LATER_ONBOARDING.SUBSCRIPTION_SUBTEXT}>
            {props.subscription}
          </SubscriptionSubText>
          <TextItemContainer>
            {items.map(item => (
              // Disabling ESLint for this line for no reason other than the code existed like this before this ESLint rule went into effect.
              // If you're here from the future, please resolve this ESLint error!
              // As-is, this may lead to unpredictable, difficult-to-detect behavior from React.
              // eslint-disable-next-line react/jsx-key
              <ItemContainer imgSrc={item.imageSource} text={item.text} alt={item.alt} />
            ))}
          </TextItemContainer>
          {/* TODO: Update this to use web-components once updated */}
          {props.isTieredRegEnabled && !props.subscriptionIncludesLater && (
            <MenuContainer>
              <FloatingHeader>
                <Header>Plus you also get </Header>
              </FloatingHeader>
              <MenuItem>
                <ImageIcon>
                  <img src={SpendImage} width="100%" alt="spend checking account" />
                </ImageIcon>
                Spend checking account
              </MenuItem>
            </MenuContainer>
          )}
          <Separator extraMargin />
          <Button
            onPress={props.handleGetStarted}
            disabled={props.isSubmitting}
            data-testid={TestIDs.WELCOME.LATER_ONBOARDING.SUBMIT_CTA}
          >
            {props.isSubmitting ? <Spinner height={20} width={20} white={true} /> : props.cta}
          </Button>
        </Container>
      </Layout>
      <ConfirmationModal
        onRequestClose={props.onRequestClose}
        isOpen={!props.retirementEligible && props.showModals}
        actions={[
          {
            label: 'Okay, got it!',
            type: ModalActionTypes.primary,
            handler: props.handleRedirect,
          },
        ]}
        message="You have to be under 69 years old to open an Acorns Later IRA, but you can keep growing wealth in your existing Acorns account!"
        title="Age limit met"
      />
      <ConfirmationModal
        onRequestClose={props.onRequestClose}
        isOpen={props.outsideIncomeEligible && props.showModals}
        actions={[
          {
            type: ModalActionTypes.primary,
            label: 'Yes',
            handler: props.handleOutsideIncomeEligibility,
          },
          {
            type: ModalActionTypes.primary,
            label: 'No',
            handler: props.handleOutsideIncomeEligibilityClose,
          },
        ]}
        message={`Do you have income in the US aside from ${props.state} income?`}
        title="Income"
      />
      <ConfirmationModal
        onRequestClose={props.onRequestClose}
        isOpen={!props.hasOutsideIncome && props.showModals}
        image={AlertBadge}
        actions={[
          {
            label: getRemoteStrings(
              'later_welcome_us_territories_unqualified_modal_learn_more',
              'Learn More',
              'laterOnboarding',
            ),
            type: ModalActionTypes.primary,
            handler: props.handleLearnMore,
          },
          {
            label: getRemoteStrings(
              'later_welcome_us_territories_unqualified_modal_cta',
              'Okay, Got it',
              'laterOnboarding',
            ),
            type: ModalActionTypes.primary,
            handler: props.handleRedirect,
          },
        ]}
        message={getRemoteStrings(
          'later_welcome_us_territories_unqualified_modal_body',
          'Unfortunately, you are required to have income from the U.S. in order to qualify for Acorns Later.',
          'laterOnboarding',
        )}
        title={getRemoteStrings(
          'later_welcome_us_territories_unqualified_modal_title',
          'Unqualified for Later',
          'laterOnboarding',
        )}
      />
      {props.isTieredRegEnabled && (
        <ConfirmationModal
          onRequestClose={props.onRequestClose}
          isOpen={
            !props.subscriptionIncludesLater &&
            !props.outsideIncomeEligible &&
            props.retirementEligible &&
            props.showModals
          }
          actions={[
            {
              type: ModalActionTypes.secondary,
              label: 'Nevermind',
              handler: props.closeUpgradeUserToSubscription,
            },
            {
              type: ModalActionTypes.primary,
              label: 'Upgrade',
              handler: props.upgradeUserToSubscription,
            },
          ]}
          message={`Adding this account will  upgrade your subscription from $${props.currentTierPrice} to $${props.tierPrice} a month.`}
          title="Confirm Upgrade"
        />
      )}
    </>
  )
}

export default enhance(LaterOnboardingWelcome)
